/**
 * ChartTemplateItemFormModal — panel ITE „Wzorce planu kont" (create/edit pozycji wzorca).
 *
 * Mirror `ChartOfAccountFormModal` (tenant, Ustawienia → Plan kont) — te same pola konta
 * (accountNumber/name/parentId/accountType/normalSide/isSettlement/isPostable), bez
 * `isActive`/`isBlocked` (pozycja wzorca nie ma tych flag — nabiera je dopiero
 * skonkretyzowane konto firmy przy adopcji, zawsze `isActive=true, isBlocked=false`,
 * patrz `docs/ADR/0081-plan-kont-wzorce-globalne-copy-on-adopt.md`).
 *
 * `parentOptions` — spłaszczone drzewo pozycji TEGO SAMEGO wzorca (wcięty label wg
 * głębokości), z wykluczoną edytowaną pozycją i jej potomkami (unika oczywistego cyklu
 * w UI; backend i tak re-waliduje PARENT_CYCLE/PARENT_NOT_IN_TEMPLATE).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import type { ChartTemplateItemFormValues } from '@/lib/chart-of-accounts-templates/api';

export interface ChartTemplateItemParentOption {
  id: string;
  accountNumber: string;
  name: string;
  depth: number;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: ChartTemplateItemFormValues;
  saving?: boolean;
  serverErrors?: Record<string, string>;
  parentOptions: ChartTemplateItemParentOption[];
  onSubmit: (values: ChartTemplateItemFormValues) => void;
  onClose: () => void;
}

export function ChartTemplateItemFormModal({
  open,
  mode,
  initialValues,
  saving = false,
  serverErrors,
  parentOptions,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) setErrors((prev) => ({ ...prev, ...serverErrors }));
  }, [serverErrors]);

  function set<K extends keyof ChartTemplateItemFormValues>(
    key: K,
    value: ChartTemplateItemFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.accountNumber.trim()) {
      next.accountNumber = t('admin.chartTemplates.itemForm.errors.accountNumberRequired');
    } else if (values.accountNumber.trim().length > 30) {
      next.accountNumber = t('admin.chartTemplates.itemForm.errors.accountNumberLength');
    }
    if (!values.name.trim()) {
      next.name = t('admin.chartTemplates.itemForm.errors.nameRequired');
    } else if (values.name.trim().length > 255) {
      next.name = t('admin.chartTemplates.itemForm.errors.nameLength');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({ ...values, accountNumber: values.accountNumber.trim(), name: values.name.trim() });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        mode === 'create'
          ? 'admin.chartTemplates.itemForm.titleCreate'
          : 'admin.chartTemplates.itemForm.titleEdit',
      )}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="chart-template-item-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="chart-template-item-form">
        <FormField
          label={t('admin.chartTemplates.itemForm.accountNumber')}
          required
          error={errors.accountNumber}
        >
          <Input
            value={values.accountNumber}
            onChange={(e) => set('accountNumber', e.target.value)}
            maxLength={30}
            data-testid="chart-template-item-form-account-number"
          />
        </FormField>

        <FormField label={t('admin.chartTemplates.itemForm.name')} required error={errors.name}>
          <Input
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            maxLength={255}
            data-testid="chart-template-item-form-name"
          />
        </FormField>

        <FormField label={t('admin.chartTemplates.itemForm.parentId')}>
          <Dropdown
            value={values.parentId}
            onChange={(v) => set('parentId', v)}
            data-testid="chart-template-item-form-parent"
            options={[
              { value: '', label: t('admin.chartTemplates.itemForm.parentNone') },
              ...parentOptions.map((opt) => ({
                value: opt.id,
                label: '  '.repeat(opt.depth) + opt.accountNumber + ' — ' + opt.name,
              })),
            ]}
          />
        </FormField>

        <FormField label={t('admin.chartTemplates.itemForm.accountType')} required>
          <Dropdown
            value={values.accountType}
            onChange={(v) => set('accountType', v as ChartTemplateItemFormValues['accountType'])}
            data-testid="chart-template-item-form-account-type"
            options={[
              { value: 'bilansowe', label: t('tenant.chartOfAccounts.accountTypes.bilansowe') },
              { value: 'wynikowe', label: t('tenant.chartOfAccounts.accountTypes.wynikowe') },
              {
                value: 'pozabilansowe',
                label: t('tenant.chartOfAccounts.accountTypes.pozabilansowe'),
              },
            ]}
          />
        </FormField>

        <FormField label={t('admin.chartTemplates.itemForm.normalSide')}>
          <Dropdown
            value={values.normalSide}
            onChange={(v) => set('normalSide', v as ChartTemplateItemFormValues['normalSide'])}
            data-testid="chart-template-item-form-normal-side"
            options={[
              { value: '', label: t('tenant.chartOfAccounts.normalSides.none') },
              { value: 'debit', label: t('tenant.chartOfAccounts.normalSides.debit') },
              { value: 'credit', label: t('tenant.chartOfAccounts.normalSides.credit') },
            ]}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <Checkbox
            label={t('admin.chartTemplates.itemForm.isSettlement')}
            checked={values.isSettlement}
            onChange={(e) => set('isSettlement', e.target.checked)}
            data-testid="chart-template-item-form-is-settlement"
          />
          <Checkbox
            label={t('admin.chartTemplates.itemForm.isPostable')}
            checked={values.isPostable}
            onChange={(e) => set('isPostable', e.target.checked)}
            data-testid="chart-template-item-form-is-postable"
          />
        </div>
      </div>
    </Modal>
  );
}
